import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { differenceInDays } from 'date-fns';
import classNames from 'classnames';

// Models
import { HotelFareModel } from '@src/shared/src/models';
// Utils
import { formatCents } from '@src/shared/src/util/general';
import useBreakpoints from '@toolkit/util/useBreakpoints';
// Constants
import {
  ACCOMMODATION_TYPES,
  CENTS_CONVERSION_TYPES,
  ENVIRONMENT,
} from '@src/shared/src/const/app';
// Store
import { IRootState } from '@src/store';
// Constants
import { FARE_PAYMENT_TYPE } from '@src/util/const';
// Components
import { HotelFareBreakfastInfo } from './HotelFareBreakfastInfo';
import { HotelFareRefundableUntil } from './HotelFareRefundableUntil';
import { HotelFareSelectionButton } from './HotelFareSelectionButton';
import { Co2EmissionLabel } from '@src/components/Co2EmissionLabel';
import { NonCompliantBadge } from '@src/components/NonCompliantBadge';

type Props = {
  fare: HotelFareModel;
  accommodationType: ACCOMMODATION_TYPES;
  env: ENVIRONMENT;
  isBasketEditable?: boolean;
  onSelectHotel?: (hotelFareIds: string[]) => void;
  isSelectingHotel?: boolean;
};

export const HotelFare: React.FC<Props> = ({
  fare,
  accommodationType,
  env,
  isBasketEditable,
  onSelectHotel,
  isSelectingHotel,
}) => {
  const { t } = useTranslation();
  const { isMd } = useBreakpoints();
  const search = useSelector((state: IRootState) => state.search.currentSearch);
  const nights = differenceInDays(search.arrAt, search.depAt);
  const calculatePricePerNight = (totalPrice: number) =>
    formatCents(totalPrice / nights, CENTS_CONVERSION_TYPES.FLOOR);

  const isMonthlyEstimate = fare.isMonthlyEstimate;

  const hotelFareClass = classNames('hotel-fare-group-item', {
    'hotel-fare-group-item-non-compliant': !fare.compliant,
  });

  return (
    <div className={hotelFareClass}>
      <div className="hotel-fare-group-item-features">
        <div className="hotel-fare-group-item-feature">
          <HotelFareBreakfastInfo fare={fare} />
        </div>
        <div className="hotel-fare-group-item-feature">
          <HotelFareRefundableUntil
            refundableUntil={fare.refundableUntil}
            conditions={fare.conditions}
            accommodationType={accommodationType}
            deepLink={fare.deepLink}
          />
        </div>
        <div className="hotel-fare-group-item-tags">
          {fare.paymentType === FARE_PAYMENT_TYPE.PREPAY && (
            <span
              className="hotel-fare-group-item-tag"
              data-rh={t('hotelFare.cell.price.tooltip.prepay')}>
              <i className="material-symbols">toll</i>
              {t('hotelFare.select.paymentType.prepaid')}
            </span>
          )}
          {fare.negotiatedRate && (
            <span
              className="hotel-fare-group-item-tag hotel-fare-group-item-tag-company"
              data-rh={t('hotel.tooltip.fare.negotiatedRates')}>
              <i className="material-symbols">attach_money</i>
              {t('hotel.fare.negotiatedRates')}
            </span>
          )}
        </div>
      </div>
      <div className="hotel-fare-group-item-price">
        <div className="hotel-fare-group-item-price-header">
          <div
            className="hotel-fare-group-item-price-total"
            data-rh={isMonthlyEstimate ? t('hotelPrice.tooltip.monthlyEstimate') : undefined}>
            {isMonthlyEstimate ? '~ ' : ''}
            {formatCents(fare.price, CENTS_CONVERSION_TYPES.FLOOR)}
          </div>
          {!fare.compliant && isMd && <NonCompliantBadge pill />}
        </div>
        <span data-rh={t('trip.tooltip.body.text.co2ValueDescription')} data-rh-at="bottom">
          <Co2EmissionLabel value={fare.co2InKg} />
        </span>
        <div className="hotel-fare-group-item-price-details">
          {isMonthlyEstimate ? (
            <>
              {t('hotelFare.label.fromPerMonth', {
                price: formatCents(fare.monthlyPrice, CENTS_CONVERSION_TYPES.FLOOR),
              })}
            </>
          ) : (
            <>
              {calculatePricePerNight(fare.price)}&nbsp;{t('hotelFare.label.pricePerNight')}
            </>
          )}
          {fare.provider && (
            <span className="hotel-fare-group-item-price-provider">
              {t('hotelFare.cell.price.provider.via')} {fare.provider}
            </span>
          )}
        </div>
      </div>
      <HotelFareSelectionButton
        fareId={fare.id}
        farePaymentType={fare.paymentType}
        fareCompliant={fare.compliant}
        env={env}
        isBasketEditable={isBasketEditable}
        onSelectHotel={onSelectHotel}
        isSelectingHotel={isSelectingHotel}
      />
    </div>
  );
};
